<template>
  <div class="ai-conversations">
    <!-- 提示訊息 -->
    <transition name="fade">
      <div v-if="showAlert"
           :class="['alert', alertType === 'success' ? 'alert-success' : 'alert-danger']"
           style="position: fixed; bottom: 20px; right: 20px; z-index: 9999; min-width: 300px;">
        <i :class="['bi', 'me-2', alertType === 'success' ? 'bi-check-circle' : 'bi-exclamation-circle']"></i>
        {{ alertMessage }}
      </div>
    </transition>

    <div class="container-fluid py-4">
      <h2 class="h4 fw-medium mb-4">{{ $t('__aiConversationReviewSystem') }}</h2>

      <!-- 統計資訊 -->
      <div class="row mb-4">
        <div class="col-md-2">
          <div class="card bg-primary text-white">
            <div class="card-body p-3">
              <div class="d-flex justify-content-between align-items-center">
                <div>
                  <h6 class="mb-0">{{ $t('__totalConversations') }}</h6>
                  <h4 class="mb-0">{{ statistics.total || 0 }}</h4>
                </div>
                <i class="bi bi-chat-dots fs-2"></i>
              </div>
            </div>
          </div>
        </div>
        <div class="col-md-2">
          <div class="card bg-success text-white">
            <div class="card-body p-3">
              <div class="d-flex justify-content-between align-items-center">
                <div>
                  <h6 class="mb-0">{{ $t('__reviewed') }}</h6>
                  <h4 class="mb-0">{{ statistics.reviewed || 0 }}</h4>
                </div>
                <i class="bi bi-check-circle fs-2"></i>
              </div>
            </div>
          </div>
        </div>
        <div class="col-md-2">
          <div class="card bg-warning text-white">
            <div class="card-body p-3">
              <div class="d-flex justify-content-between align-items-center">
                <div>
                  <h6 class="mb-0">{{ $t('__hasIssue') }}</h6>
                  <h4 class="mb-0">{{ statistics.has_issue || 0 }}</h4>
                </div>
                <i class="bi bi-exclamation-triangle fs-2"></i>
              </div>
            </div>
          </div>
        </div>
        <div class="col-md-2">
          <div class="card bg-info text-white">
            <div class="card-body p-3">
              <div class="d-flex justify-content-between align-items-center">
                <div>
                  <h6 class="mb-0">{{ $t('__learned') }}</h6>
                  <h4 class="mb-0">{{ statistics.learned || 0 }}</h4>
                </div>
                <i class="bi bi-lightbulb fs-2"></i>
              </div>
            </div>
          </div>
        </div>
        <div class="col-md-2">
          <div class="card bg-secondary text-white">
            <div class="card-body p-3">
              <div class="d-flex justify-content-between align-items-center">
                <div>
                  <h6 class="mb-0">{{ $t('__todayNew') }}</h6>
                  <h4 class="mb-0">{{ statistics.today || 0 }}</h4>
                </div>
                <i class="bi bi-calendar-day fs-2"></i>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- 搜尋列 -->
      <div class="d-flex align-items-center justify-content-between mb-3 flex-wrap">
        <!-- 群組篩選 -->
        <div class="d-flex align-items-center me-2 mb-2">
          <div class="device-report-select">
            <select v-model="filters.group_id" class="form-select rounded-pill me-2" @change="fetchConversations">
              <option value="">{{ $t('__allGroups') }}</option>
              <option v-for="group in groups" :key="group.group_id" :value="group.group_id">
                {{ group.group_name || group.group_id }}
              </option>
            </select>
          </div>
        </div>

        <!-- 狀態篩選 -->
        <div class="d-flex align-items-center me-2 mb-2">
          <div class="d-flex align-items-center me-3">
            <div class="status-switch me-2">
              <input
                type="checkbox"
                id="showReviewed"
                v-model="filters.reviewed"
                @change="fetchConversations"
              />
              <label for="showReviewed"></label>
            </div>
            <span class="text-muted">{{ $t('__showReviewedOnly') }}</span>
          </div>
          <div class="d-flex align-items-center">
            <div class="status-switch me-2">
              <input
                type="checkbox"
                id="showIssue"
                v-model="filters.has_issue"
                @change="fetchConversations"
              />
              <label for="showIssue"></label>
            </div>
            <span class="text-muted">{{ $t('__showIssueOnly') }}</span>
          </div>
        </div>

        <!-- 日期篩選 -->
        <div class="d-flex align-items-center me-2 mb-2">
          <VueCtkDateTimePicker
            v-model="filters.date_from"
            only-date
            format="YYYY-MM-DD"
            formatted="YYYY-MM-DD"
            color="#2CBDC0"
            button-color="#2CBDC0"
            :label="$t('__startDate')"
            class="me-2"
            @input="fetchConversations"
          />
          <span class="mx-2">～</span>
          <VueCtkDateTimePicker
            v-model="filters.date_to"
            only-date
            format="YYYY-MM-DD"
            formatted="YYYY-MM-DD"
            color="#2CBDC0"
            button-color="#2CBDC0"
            :label="$t('__endDate')"
            @input="fetchConversations"
          />
        </div>

        <!-- 搜尋框 -->
        <form @submit.prevent="fetchConversations" class="me-2 mb-2">
          <div class="input-group rounded-pill">
            <input
              v-model="filters.search"
              type="text"
              class="form-control rounded-pill"
              :placeholder="$t('__searchConversationPlaceholder')"
              aria-label="search"
            />
            <button type="submit" class="btn btn-link px-2 text-dark">
              <i class="bi bi-search"></i>
            </button>
            <button
              v-if="filters.search"
              type="button"
              class="btn btn-primary px-3 ms-1 rounded-pill"
              @click="clearSearch"
            >
              {{ $t('__clearSearch') }}
            </button>
          </div>
        </form>
      </div>

      <!-- 對話列表 -->
      <div class="card">
        <div v-if="loading" class="card-body text-center">
          <i class="fas fa-spinner fa-spin"></i> {{ $t('__loading') }}
        </div>
        <div v-else-if="conversations && conversations.length > 0" class="card-body">
          <b-table
            :responsive="$store.state.windowWidth > $store.state.breakpoints.sm"
            :stacked="$store.state.windowWidth <= $store.state.breakpoints.sm"
            striped
            borderless
            no-provider-filtering
            head-variant="light"
            :items="conversations"
            :fields="fields"
          >
            <template v-slot:head()="scope">
              <div class="text-nowrap">
                {{ scope.label }}
              </div>
            </template>

            <template v-slot:cell(group)="data">
              <small>{{ data.item.group_name || data.item.group_id || '-' }}</small>
            </template>

            <template v-slot:cell(time)="data">
              <small>{{ formatDate(data.item.created_at) }}</small>
            </template>

            <template v-slot:cell(question)="data">
              <div class="text-truncate" style="max-width: 300px" :title="data.item.question">
                {{ data.item.question }}
              </div>
            </template>

            <template v-slot:cell(answer)="data">
              <div class="text-truncate" style="max-width: 300px" :title="data.item.answer">
                {{ data.item.answer }}
              </div>
            </template>

            <template v-slot:cell(confidence)="data">
              <div class="progress" style="height: 20px;">
                <div
                  class="progress-bar"
                  :class="getConfidenceClass(data.item.confidence)"
                  role="progressbar"
                  :style="`width: ${(data.item.confidence * 100).toFixed(0)}%`"
                >
                  {{ (data.item.confidence * 100).toFixed(0) }}%
                </div>
              </div>
            </template>

            <template v-slot:cell(status)="data">
              <span v-if="data.item.reviewed" class="badge bg-success me-1">
                {{ $t('__reviewed') }}
              </span>
              <span v-if="data.item.has_issue" class="badge bg-warning me-1">
                {{ $t('__hasIssue') }}
              </span>
              <span v-if="data.item.learned" class="badge bg-info me-1">
                {{ $t('__learned') }}
              </span>
              <span v-if="!data.item.reviewed && !data.item.has_issue" class="badge bg-secondary">
                {{ $t('__pendingReview') }}
              </span>
            </template>

            <template v-slot:cell(actions)="data">
              <button
                @click="viewDetail(data.item)"
                class="btn btn-sm btn-outline-primary me-1"
                :title="$t('__viewDetail')"
              >
                <i class="bi bi-eye"></i>
              </button>
              <button
                @click="showReviewModal(data.item)"
                class="btn btn-sm btn-outline-warning"
                :title="$t('__reviewProcess')"
              >
                <i class="bi bi-pencil-square"></i>
              </button>
            </template>
          </b-table>
        </div>
        <div v-else class="card-body text-center text-muted">
          {{ $t('__noConversationRecords') }}
        </div>
      </div>

      <!-- 分頁 -->
      <div v-if="totalPages > 1" class="d-flex justify-content-center mt-4">
        <nav aria-label="Page navigation">
          <ul class="pagination">
            <li class="page-item" :class="{ disabled: currentPage === 1 }">
              <button class="page-link" @click="changePage(currentPage - 1)" :disabled="currentPage === 1">
                {{ $t('__previousPage') }}
              </button>
            </li>
            <li
              v-for="page in displayPages"
              :key="page"
              class="page-item"
              :class="{ active: page === currentPage }"
            >
              <button class="page-link" @click="changePage(page)">
                {{ page }}
              </button>
            </li>
            <li class="page-item" :class="{ disabled: currentPage === totalPages }">
              <button class="page-link" @click="changePage(currentPage + 1)" :disabled="currentPage === totalPages">
                {{ $t('__nextPage') }}
              </button>
            </li>
          </ul>
        </nav>
      </div>
    </div>

    <!-- 詳情 Modal -->
    <div class="modal fade" id="detailModal" tabindex="-1">
      <div class="modal-dialog modal-lg">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">{{ $t('__conversationDetail') }}</h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
          </div>
          <div class="modal-body" v-if="selectedConversation">
            <div class="mb-3">
              <label class="form-label fw-bold">{{ $t('__group') }}：</label>
              <p>{{ selectedConversation.group_name || selectedConversation.group_id || '-' }}</p>
            </div>
            <div class="mb-3">
              <label class="form-label fw-bold">{{ $t('__time') }}：</label>
              <p>{{ formatDateTime(selectedConversation.created_at) }}</p>
            </div>
            <div class="mb-3">
              <label class="form-label fw-bold">{{ $t('__userQuestion') }}：</label>
              <p class="bg-light p-3 rounded">{{ selectedConversation.question }}</p>
            </div>
            <div class="mb-3">
              <label class="form-label fw-bold">{{ $t('__aiAnswer') }}：</label>
              <p class="bg-light p-3 rounded">{{ selectedConversation.answer }}</p>
            </div>
            <div class="mb-3">
              <label class="form-label fw-bold">{{ $t('__confidence') }}：</label>
              <div class="progress" style="height: 25px;">
                <div
                  class="progress-bar"
                  :class="getConfidenceClass(selectedConversation.confidence)"
                  role="progressbar"
                  :style="`width: ${(selectedConversation.confidence * 100).toFixed(0)}%`"
                >
                  {{ (selectedConversation.confidence * 100).toFixed(0) }}%
                </div>
              </div>
            </div>
            <div v-if="selectedConversation.corrected_answer" class="mb-3">
              <label class="form-label fw-bold">{{ $t('__correctedAnswer') }}：</label>
              <p class="bg-success bg-opacity-10 p-3 rounded">{{ selectedConversation.corrected_answer }}</p>
            </div>
            <div v-if="selectedConversation.knowledge_items && selectedConversation.knowledge_items.length" class="mb-3">
              <label class="form-label fw-bold">{{ $t('__usedKnowledgeBase') }}：</label>
              <ul class="list-group">
                <li v-for="knowledge in selectedConversation.knowledge_items" :key="knowledge.id" class="list-group-item">
                  <small class="text-muted">#{{ knowledge.id }}</small>
                  {{ knowledge.question || knowledge.title }}
                </li>
              </ul>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">{{ $t('__close') }}</button>
          </div>
        </div>
      </div>
    </div>

    <!-- 審核處理 Modal -->
    <div class="modal fade" id="reviewModal" tabindex="-1">
      <div class="modal-dialog modal-lg">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">{{ $t('__reviewProcess') }}</h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
          </div>
          <div class="modal-body" v-if="reviewingConversation">
            <div class="mb-3">
              <label class="form-label fw-bold">{{ $t('__question') }}：</label>
              <p class="bg-light p-3 rounded">{{ reviewingConversation.question }}</p>
            </div>
            <div class="mb-3">
              <label class="form-label fw-bold">{{ $t('__aiAnswer') }}：</label>
              <p class="bg-light p-3 rounded">{{ reviewingConversation.answer }}</p>
            </div>

            <div class="mb-3">
              <label class="form-label fw-bold">{{ $t('__processingMethod') }}：</label>
              <div class="form-check">
                <input
                  class="form-check-input"
                  type="radio"
                  name="actionType"
                  id="actionPass"
                  value="pass"
                  v-model="reviewForm.action"
                >
                <label class="form-check-label" for="actionPass">
                  {{ $t('__answerCorrectPass') }}
                </label>
              </div>
              <div class="form-check">
                <input
                  class="form-check-input"
                  type="radio"
                  name="actionType"
                  id="actionNewFaq"
                  value="new_faq"
                  v-model="reviewForm.action"
                >
                <label class="form-check-label" for="actionNewFaq">
                  {{ $t('__createNewFaqRecommended') }}
                </label>
              </div>
              <div class="form-check">
                <input
                  class="form-check-input"
                  type="radio"
                  name="actionType"
                  id="actionUpdate"
                  value="update_knowledge"
                  v-model="reviewForm.action"
                >
                <label class="form-check-label" for="actionUpdate">
                  {{ $t('__updateExistingKnowledge') }}
                </label>
              </div>
            </div>

            <!-- 建立新 FAQ 表單 -->
            <div v-if="reviewForm.action === 'new_faq'" class="border rounded p-3 mb-3">
              <h6>{{ $t('__createNewFaq') }}</h6>
              <div class="mb-2">
                <label class="form-label">{{ $t('__question') }}：</label>
                <input
                  type="text"
                  class="form-control"
                  v-model="reviewForm.newQuestion"
                  :placeholder="reviewingConversation.question"
                >
              </div>
              <div class="mb-2">
                <label class="form-label">{{ $t('__correctAnswer') }}：<span class="text-danger">*</span></label>
                <textarea
                  class="form-control"
                  rows="3"
                  v-model="reviewForm.correctAnswer"
                  :placeholder="$t('__inputCorrectAnswer')"
                  required
                ></textarea>
              </div>
            </div>

            <!-- 修正現有知識 -->
            <div v-if="reviewForm.action === 'update_knowledge'" class="border rounded p-3 mb-3">
              <h6>{{ $t('__updateExistingKnowledge') }}</h6>
              <div class="mb-2">
                <label class="form-label">{{ $t('__selectKnowledgeToUpdate') }}：</label>
                <select class="form-select" v-model="reviewForm.knowledgeId">
                  <option value="">{{ $t('__pleaseSelect') }}...</option>
                  <option
                    v-for="knowledge in reviewingConversation.knowledge_items"
                    :key="knowledge.id"
                    :value="knowledge.id"
                  >
                    #{{ knowledge.id }} - {{ knowledge.question || knowledge.title }}
                  </option>
                </select>
              </div>
              <div class="mb-2">
                <label class="form-label">{{ $t('__correctAnswer') }}：<span class="text-danger">*</span></label>
                <textarea
                  class="form-control"
                  rows="3"
                  v-model="reviewForm.correctAnswer"
                  :placeholder="$t('__inputCorrectAnswer')"
                  required
                ></textarea>
              </div>
            </div>

          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">{{ $t('__cancel') }}</button>
            <button
              type="button"
              class="btn btn-primary"
              @click="submitReview"
              :disabled="!canSubmitReview"
            >
              {{ $t('__confirmProcess') }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Modal } from "bootstrap";
import { aiConversations } from "@/http/api/aiConversations";
import VueCtkDateTimePicker from 'vue-ctk-date-time-picker';
import 'vue-ctk-date-time-picker/dist/vue-ctk-date-time-picker.css';

export default {
  name: "AiConversations",
  components: { VueCtkDateTimePicker },
  data() {
    return {
      conversations: [],
      groups: [],
      statistics: {},
      filters: {
        group_id: "",
        reviewed: false,
        has_issue: false,
        date_from: "",
        date_to: "",
        search: "",
      },
      loading: false,
      currentPage: 1,
      totalPages: 1,
      perPage: 20,
      selectedConversation: null,
      reviewingConversation: null,
      // 提示訊息
      showAlert: false,
      alertMessage: "",
      alertType: "success",
      reviewForm: {
        action: "pass",
        newQuestion: "",
        correctAnswer: "",
        knowledgeId: "",
      },
      detailModal: null,
      reviewModal: null,
      fields: [
        { key: 'group', label: this.$t('__group'), thStyle: { width: '150px' }, tdClass: 'align-middle' },
        { key: 'time', label: this.$t('__time'), thStyle: { width: '150px' }, tdClass: 'align-middle' },
        { key: 'question', label: this.$t('__question'), tdClass: 'align-middle' },
        { key: 'answer', label: this.$t('__aiAnswer'), tdClass: 'align-middle' },
        { key: 'confidence', label: this.$t('__confidence'), thStyle: { width: '100px' }, tdClass: 'align-middle' },
        { key: 'status', label: this.$t('__status'), thStyle: { width: '100px' }, tdClass: 'align-middle' },
        { key: 'actions', label: this.$t('__operation'), thStyle: { width: '150px' }, tdClass: 'align-middle' }
      ],
    };
  },
  computed: {
    displayPages() {
      const pages = [];
      const start = Math.max(1, this.currentPage - 2);
      const end = Math.min(this.totalPages, this.currentPage + 2);

      for (let i = start; i <= end; i++) {
        pages.push(i);
      }
      return pages;
    },
    canSubmitReview() {
      if (this.reviewForm.action === "pass") {
        return true;
      }
      if (this.reviewForm.action === "new_faq") {
        return this.reviewForm.correctAnswer.trim() !== "";
      }
      if (this.reviewForm.action === "update_knowledge") {
        return this.reviewForm.knowledgeId && this.reviewForm.correctAnswer.trim() !== "";
      }
      return false;
    },
  },
  methods: {
    // 顯示提示訊息
    showToast(message, type = 'success') {
      this.alertMessage = message;
      this.alertType = type;
      this.showAlert = true;

      setTimeout(() => {
        this.showAlert = false;
      }, 3000);
    },

    async fetchConversations() {
      console.log(this.$t('__loadingConversations'));
      this.loading = true;
      try {
        const params = {
          page: this.currentPage,
          per_page: this.perPage,
          ...this.filters,
        };

        // 移除空值
        Object.keys(params).forEach(key => {
          if (params[key] === "" || params[key] === false) {
            delete params[key];
          }
        });

        console.log("呼叫 API with params:", params);
        const token = localStorage.getItem('humetrics_token');
        const response = await aiConversations.get(params, token);
        console.log("API 回應:", response.data);

        this.conversations = response.data.data;
        this.currentPage = response.data.current_page;
        this.totalPages = response.data.last_page;
      } catch (error) {
        console.error("Failed to fetch conversations:", error);
        console.error("Error details:", error.response);
        this.showToast(error.response?.data?.message || this.$t('__failedToLoadConversations'), "error");
      } finally {
        this.loading = false;
      }
    },

    async fetchGroups() {
      try {
        console.log(this.$t('__loadingGroups'));
        const token = localStorage.getItem('humetrics_token');
        const response = await aiConversations.getGroups(token);
        console.log("群組列表:", response.data);
        this.groups = response.data.data;
      } catch (error) {
        console.error("Failed to fetch groups:", error);
        console.error("Error details:", error.response);
      }
    },

    async fetchStatistics() {
      try {
        console.log(this.$t('__loadingStatistics'));
        const token = localStorage.getItem('humetrics_token');
        const response = await aiConversations.getStatistics(token);
        console.log("統計資料:", response.data);
        this.statistics = response.data.data;
      } catch (error) {
        console.error("Failed to fetch statistics:", error);
        console.error("Error details:", error.response);
      }
    },

    async viewDetail(conversation) {
      try {
        const token = localStorage.getItem('humetrics_token');
        const response = await aiConversations.getById(conversation.id, token);
        this.selectedConversation = response.data.data;
        this.detailModal.show();
      } catch (error) {
        console.error("Failed to fetch conversation detail:", error);
        this.showToast(this.$t('__failedToLoadDetail'), "error");
      }
    },

    showReviewModal(conversation) {
      this.reviewingConversation = conversation;
      this.reviewForm = {
        action: "pass",
        newQuestion: conversation.question,
        correctAnswer: "",
        knowledgeId: "",
      };

      // 如果有使用的知識，載入詳情
      if (conversation.knowledge_ids && conversation.knowledge_ids.length > 0) {
        this.viewDetailForReview(conversation);
      }

      this.reviewModal.show();
    },

    async viewDetailForReview(conversation) {
      try {
        const token = localStorage.getItem('humetrics_token');
        const response = await aiConversations.getById(conversation.id, token);
        this.reviewingConversation = response.data.data;
      } catch (error) {
        console.error("Failed to fetch conversation detail:", error);
      }
    },

    async submitReview() {
      try {
        const conversationId = this.reviewingConversation.id;
        const token = localStorage.getItem('humetrics_token');

        if (this.reviewForm.action === "pass") {
          // 標記為已審核，無問題
          await aiConversations.review(conversationId, {
            reviewed: true,
            has_issue: false,
            feedback: "good",
          }, token);
          this.showToast(this.$t('__markedAsReviewed'));

          // 更新當前項目狀態
          const index = this.conversations.findIndex(c => c.id === conversationId);
          if (index !== -1) {
            this.conversations[index].reviewed = true;
            this.conversations[index].has_issue = false;
          }

        } else if (this.reviewForm.action === "new_faq") {
          // 建立新 FAQ
          await aiConversations.createFaq(conversationId, {
            question: this.reviewForm.newQuestion,
            answer: this.reviewForm.correctAnswer,
          }, token);
          this.showToast(this.$t('__newFaqCreated'));

          // 更新當前項目狀態
          const index = this.conversations.findIndex(c => c.id === conversationId);
          if (index !== -1) {
            this.conversations[index].reviewed = true;
            this.conversations[index].learned = true;
          }

        } else if (this.reviewForm.action === "update_knowledge") {
          // 修正現有知識
          await aiConversations.updateKnowledge(conversationId, {
            knowledge_id: this.reviewForm.knowledgeId,
            answer: this.reviewForm.correctAnswer,
          }, token);
          this.showToast(this.$t('__knowledgeBaseUpdated'));

          // 更新當前項目狀態
          const index = this.conversations.findIndex(c => c.id === conversationId);
          if (index !== -1) {
            this.conversations[index].reviewed = true;
            this.conversations[index].learned = true;
          }
        }

        this.reviewModal.hide();
        // 只更新統計資料，不重新載入整個列表
        this.fetchStatistics();
      } catch (error) {
        console.error("Failed to submit review:", error);
        this.showToast(error.response?.data?.message || this.$t('__processFailed'), "error");
      }
    },

    clearSearch() {
      this.filters.search = "";
      this.fetchConversations();
    },

    changePage(page) {
      if (page >= 1 && page <= this.totalPages) {
        this.currentPage = page;
        this.fetchConversations();
      }
    },

    formatDate(date) {
      if (!date) return "-";
      const d = new Date(date);
      return `${d.getMonth() + 1}/${d.getDate()} ${d.getHours()}:${d.getMinutes().toString().padStart(2, "0")}`;
    },

    formatDateTime(date) {
      if (!date) return "-";
      const d = new Date(date);
      return `${d.getFullYear()}/${d.getMonth() + 1}/${d.getDate()} ${d.getHours()}:${d.getMinutes().toString().padStart(2, "0")}:${d.getSeconds().toString().padStart(2, "0")}`;
    },

    getConfidenceClass(confidence) {
      if (confidence >= 0.8) return "bg-success";
      if (confidence >= 0.5) return "bg-warning";
      return "bg-danger";
    },
  },
  mounted() {
    // 先呼叫 API
    this.fetchConversations();
    this.fetchGroups();
    this.fetchStatistics();

    // 延遲初始化 Modal，確保 DOM 已渲染
    this.$nextTick(() => {
      const detailElement = document.getElementById("detailModal");
      const reviewElement = document.getElementById("reviewModal");

      if (detailElement) {
        this.detailModal = new Modal(detailElement);
      }
      if (reviewElement) {
        this.reviewModal = new Modal(reviewElement);
      }
    });
  },
};
</script>

<style scoped>
.ai-conversations {
  min-height: 100vh;
}

.text-truncate {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.progress {
  background-color: #f0f0f0;
}

/* 提示訊息動畫 */
.fade-enter-active, .fade-leave-active {
  transition: all 0.3s ease;
}

.fade-enter, .fade-leave-to {
  opacity: 0;
  transform: translateY(30px);
}
</style>